import { useState } from "react";
import NextArrow from "@/components/landing/NextArrow";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, MapPin, Phone } from "lucide-react";
import { useBrand } from "@/brand";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND ? `${BACKEND}/api` : null;

const inputCls =
  "w-full bg-[var(--gm-surface)] border border-[var(--gm-line)] rounded-sm px-5 py-4 text-sm font-light placeholder:text-[var(--gm-faint)] transition-colors duration-300 focus:border-[var(--gm-accent)]";

const ContactSection = () => {
  const { email } = useBrand();
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "", area: "", mensaje: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.mensaje) {
      toast.error("Por favor completa nombre, correo y mensaje.");
      return;
    }
    if (!API) {
      toast.info("Este sitio es una muestra: el formulario aún no está conectado. Escríbenos por WhatsApp o correo.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contacto`, form);
      toast.success("Mensaje recibido. El despacho te contactará a la brevedad.");
      setForm({ nombre: "", email: "", telefono: "", area: "", mensaje: "" });
    } catch {
      toast.error("No se pudo enviar el mensaje. Intenta de nuevo o usa WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" className="screen py-16 md:py-20 bg-[var(--gm-paper)]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <p className="text-xs tracking-[0.3em] uppercase font-semibold text-[var(--gm-accent)] mb-6">Contacto</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
            Cuéntanos tu caso, <span className="italic text-[var(--gm-accent)]">hoy mismo</span>
          </h2>
          <p className="mt-8 text-base font-light text-[var(--gm-muted)] leading-relaxed">
            Escríbenos con calma o, si tu asunto es urgente, contáctanos directo por WhatsApp o correo.
          </p>

          <div className="mt-10 space-y-6 text-sm font-light">
            <div className="flex items-start gap-4" data-testid="contact-address">
              <MapPin className="w-5 h-5 text-[var(--gm-accent)] mt-0.5" strokeWidth={1.5} />
              <p className="leading-relaxed">
                Calzada Zavaleta No. 4710, Piso 9, Interior 4<br />
                Torre Llum, C.P. 72176, Puebla, Pue.
              </p>
            </div>
            <div className="flex items-center gap-4" data-testid="contact-phones">
              <Phone className="w-5 h-5 text-[var(--gm-accent)]" strokeWidth={1.5} />
              <p>Cel. +52 222 426 5873 · Ofic. +52 222 861 6693</p>
            </div>
          </div>

          <div className="mt-12 flex flex-wrap gap-4">
            <a
              href="https://wa.me/522224265873?text=Hola%2C%20necesito%20asesor%C3%ADa%20legal%20urgente."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-7 py-4 rounded-full text-sm bg-[#25D366] text-white transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(37,211,102,0.35)]"
              data-testid="contact-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.8} />
              WhatsApp urgente
            </a>
            <a
              href={`mailto:${email}?subject=Consulta%20legal`}
              className="btn-outline-navy flex items-center gap-3 px-7 py-4 rounded-full text-sm"
              data-testid="contact-email-btn"
            >
              <Mail className="w-4 h-4" strokeWidth={1.8} />
              Correo directo
            </a>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7 backdrop-blur-xl bg-[var(--gm-surface-70)] border border-[var(--gm-vidrio)] shadow-[0_30px_60px_rgba(11,22,44,0.08)] rounded-sm p-8 md:p-12"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <input value={form.nombre} onChange={set("nombre")} placeholder="Nombre completo *" className={inputCls} data-testid="contact-input-nombre" />
            <input value={form.email} onChange={set("email")} type="email" placeholder="Correo electrónico *" className={inputCls} data-testid="contact-input-email" />
            <input value={form.telefono} onChange={set("telefono")} placeholder="Teléfono (opcional)" className={inputCls} data-testid="contact-input-telefono" />
            <select value={form.area} onChange={set("area")} className={inputCls} data-testid="contact-select-area">
              <option value="">Área de interés</option>
              <option value="civil">Derecho Civil</option>
              <option value="mercantil">Derecho Mercantil</option>
              <option value="penal">Derecho Penal</option>
              <option value="laboral">Derecho Laboral</option>
              <option value="amparo">Amparo</option>
              <option value="otro">Otro</option>
            </select>
          </div>
          <textarea
            value={form.mensaje}
            onChange={set("mensaje")}
            rows={6}
            placeholder="Describe brevemente tu situación *"
            className={`${inputCls} mt-5 resize-none`}
            data-testid="contact-input-mensaje"
          />
          <button
            type="submit"
            disabled={sending}
            className="btn-navy mt-8 px-10 py-4 rounded-full text-sm tracking-wide disabled:opacity-60"
            data-testid="contact-submit-btn"
          >
            {sending ? "Enviando..." : "Enviar mensaje"}
          </button>
          <p className="mt-5 text-xs font-light text-[var(--gm-faint)]">
            Tu mensaje llega directo al correo del despacho. No guardamos tus datos.
          </p>
        </motion.form>
      </div>
          <NextArrow to="fin" etiqueta="Ir al final de la página" />
</section>
  );
};

export default ContactSection;
